import React, { useState, useEffect } from 'react';
import useCurrentUser from '../../hooks/useCurrentUser'
import {getAccountOwner} from '../../util/util'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as faIcons  from '@fortawesome/free-solid-svg-icons'
/**
 * TryFitSW2 Component
 * A standalone banner that:
 * 1. Sits at the top of the screen (absolute).
 * 2. Uses purely inline styles for easy customization.
 * 3. Tracks impressions and closes via localStorage.
 * 4. Disappears forever after 5 views/closes.
 */

const EXCLUDED_USERS = [
  22195,//IBP
]

const IOS_STORE_LINK = 'https://apps.apple.com/us/app/fitsw-for-personal-trainers/id1184011053'
const GOOGLE_STORE_LINK = 'https://play.google.com/store/apps/details?id=com.jcs.fitsw.fitsw20&hl=en'

const TryFitSW2 = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [stats, setStats] = useState({ views: 0, closes: 0 });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const user = useCurrentUser()

  const ownerId = getAccountOwner(user)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // 1. Retrieve existing data from localStorage
    const storedData = localStorage.getItem('tryfitsw2_stats');
    let currentStats = { views: 0, closes: 0 };

    if (storedData) {
      try {
        currentStats = JSON.parse(storedData);
      } catch (e) {
        console.error("Error parsing localStorage", e);
      }
    }

    // 2. Logic: If it has already been shown 5 times, don't show it again
    if (currentStats.views >= 5) {
      setIsVisible(false);
      setStats(currentStats);
      return;
    }

    // 3. Increment view count and save
    const updatedStats = {
      ...currentStats,
      views: currentStats.views + 1
    };

    localStorage.setItem('tryfitsw2_stats', JSON.stringify(updatedStats));
    setStats(updatedStats);
    setIsVisible(true);
  }, []);

  const handleClose = () => {
    // Increment close count in localStorage
    const updatedStats = {
      ...stats,
      closes: stats.closes + 1
    };
    localStorage.setItem('tryfitsw2_stats', JSON.stringify(updatedStats));
    setStats(updatedStats);
    setIsVisible(false);
  };

  if(!ownerId || EXCLUDED_USERS.includes(ownerId)) return null

  // If view limit reached or manually closed this session
  if (!isVisible) return null;

  if(user.isMAXUser) return null

  // --- Inline Styles Configuration ---
  const styles = {
    bannerContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      backgroundColor: '#00041c',
      color: '#ffffff',
      padding: isMobile ? '10px 12px' : '12px 20px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'stretch' : 'center',
      gap: isMobile ? '8px' : '0',
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      zIndex: 9999,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      boxSizing: 'border-box',
    },
    title: {
      margin: 0,
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      letterSpacing: '0.5px',
      color: '#ffffff',
    },
    subtitle: {
      margin: 0,
      marginTop: '4px',
      fontSize: isMobile ? '12px' : '14px',
      opacity: 0.8,
    },
    badge: {
      backgroundColor: '#1bbf47',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '10px',
      fontWeight: 'bold',
      marginLeft: '8px',
      textTransform: 'uppercase',
    },
    closeButton: {
      background: 'transparent',
      border: '1px solid rgba(255,255,255,0.3)',
      color: '#fff',
      padding: '6px 12px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '12px',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
  };

  const dismissButton = (
    <button
      onClick={handleClose}
      style={styles.closeButton}
      onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      Dismiss
    </button>
  );

  return (
    <div style={styles.bannerContainer}>
      {/* Row 1: icon + text + dismiss (mobile only) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <img
            style={{
              border: '1px solid #777',
              borderRadius: '16px',
              display: 'block',
              marginRight: '8px',
              flexShrink: 0,
            }}
            width={isMobile ? 44 : 64}
            height={isMobile ? 44 : 64}
            src="https://fitsw.s3.us-west-2.amazonaws.com/whitelabel/fitsw/fit-2.0-icon-64.png"
          />
          <div>
            <h2 style={styles.title}>
              Try Improved FitSW 2.0 App<span style={styles.badge}>NEW</span>
            </h2>
            <p style={styles.subtitle}>
              <FontAwesomeIcon style={{ color: '#0efa02' }} icon={faIcons.faCheck} className="mr-1" />
              {' '}Works with your current plan (no additional cost)
            </p>
            <p style={styles.subtitle}>
              <FontAwesomeIcon style={{ color: '#0efa02' }} icon={faIcons.faCheck} className="mr-1" />
              {' '}All your data is available (zero migration)
            </p>
          </div>
        </div>
        {isMobile && dismissButton}
      </div>

      {/* Row 2: quote + store links */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: '6px',
      }}>
        <div>"OMG it's so much better"</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={IOS_STORE_LINK}
            style={{
              backgroundColor: '#fff',
              color: '#000',
              padding: isMobile ? '6px 14px' : '8px 16px',
              borderRadius: '7px',
              textDecoration: 'none',
              fontSize: isMobile ? '13px' : '14px',
            }}
          >
            iOS
          </a>
          <a
            target="_blank"
            rel="noreferrer"
            href={GOOGLE_STORE_LINK}
            style={{
              backgroundColor: '#32DE84',
              color: '#000',
              padding: isMobile ? '6px 14px' : '8px 16px',
              borderRadius: '7px',
              textDecoration: 'none',
              fontSize: isMobile ? '13px' : '14px',
            }}
          >
            Android
          </a>
        </div>
      </div>

      {/* Dismiss button — desktop only */}
      {!isMobile && dismissButton}
    </div>
  );
};

export default TryFitSW2
